import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import ScreenReaderOnly from '../../accessibility/ScreenReaderOnly';
import RoundedButtonSecondaryAlt from '../../components/button/rounded-button-secondary-alt/RoundedButtonSecondaryAlt';
import RoundedButtonSecondary from '../../components/button/rounded-button-secondary/RoundedButtonSecondary';
import RoundedButtonPrimary from '../../components/button/rounded-button-primary/RoundedButtonPrimary';
import { isRewardLottery, rewardInGa4ItemStructure } from './reward-model';
import { addItemToCart, removeItemFromCart, updateItemInCart } from '../../cart/cart-actions';
import { useCart } from '../project-hooks/useCart';
import { useProject } from '../project-hooks/useProject';
import { isActive } from '../project-model';
import { trackEventInAnalytics } from '../../support-and-tracking/analytics-actions';
import {
    RECOMMENDED_EVENT_ADD_TO_CART,
    RECOMMENDED_EVENT_REMOVE_FROM_CART,
} from '../../support-and-tracking/analytics-events';
import styles from './reward.module.less';
import { useSubproject } from '../../components/hooks/useSubproject';
import removeShoppingCart from '@sb1/ffe-icons/icons/open/300/md/remove_shopping_cart.svg';
import { Icon } from '@sb1/ffe-icons-react';

const getDefaultValue = ({ defaultValue, quantity, purchased_count }) => {
    if (!quantity) {
        return defaultValue;
    }
    const available = quantity > 0 ? quantity - purchased_count : quantity;
    if (available < 0) {
        return defaultValue;
    }
    if (defaultValue > available) {
        return available;
    }
    return defaultValue;
};

export const RewardButtons = ({
    reward,
    canSelectMultiple,
    defaultValue,
    min,
    max,
    onClick,
    onAfterClick,
    available,
    navigateOnClick,
    showReadMoreModalButton,
    supportButtonText,
    buttonIcon,
    wrapperClassName = '',
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const project = useProject();
    const { subproject } = useSubproject();

    const cart = useCart();
    const defaultValueToUse = getDefaultValue({
        defaultValue,
        quantity: reward.quantity,
        purchased_count: reward.purchased_count,
    });

    const [count, setCount] = useState(defaultValueToUse);

    const minCount = min;
    const maxCount = available ?? max;

    const cartItem = reward && cart?.items?.find((item) => item.rewardId === reward.id);

    const currentCount = cartItem?.count || count;
    const isSoldOut = available !== null && available <= 0;

    const canBuy = !isSoldOut && isActive(project);

    const decreaseCount = () => {
        if (currentCount > minCount) {
            if (cartItem) {
                dispatch(updateItemInCart({ projectId: project.id, rewardId: reward.id, count: currentCount - 1 }));
            } else {
                setCount(currentCount - 1);
            }
        }
    };

    const increaseCount = () => {
        if (currentCount < maxCount) {
            if (cartItem) {
                dispatch(updateItemInCart({ projectId: project.id, rewardId: reward.id, count: currentCount + 1 }));
            } else {
                setCount(currentCount + 1);
            }
        }
    };

    const trackShoppingCartInGA4Format = ({ event, reward, quantity }) => {
        trackEventInAnalytics(event, {
            currency: 'NOK',
            value: reward.price * quantity,
            items: [rewardInGa4ItemStructure(reward, quantity)],
        });
    };

    const addToShoppingCart = async () => {
        trackShoppingCartInGA4Format({ event: RECOMMENDED_EVENT_ADD_TO_CART, reward, quantity: currentCount });
        await dispatch(addItemToCart({ projectId: project.id, rewardId: reward.id, count: currentCount }));
        if (navigateOnClick) {
            navigate({
                pathname: `/project/${project.id}/payment`,
                search: subproject ? `?subproject=${subproject?.id}` : undefined,
            });
        }
    };

    const removeFromShoppingCart = () => {
        trackShoppingCartInGA4Format({ event: RECOMMENDED_EVENT_REMOVE_FROM_CART, reward, quantity: currentCount });
        dispatch(removeItemFromCart({ projectId: project.id, rewardId: reward.id }));
        setCount(defaultValueToUse);
    };

    return (
        <fieldset disabled={!canBuy} className={[styles.footerButtons, wrapperClassName].join(' ')}>
            {canSelectMultiple && reward.price ? (
                <>
                    <ScreenReaderOnly>Velg antall</ScreenReaderOnly>
                    <div className={styles.selectNumberOfProducs}>
                        <RoundedButtonSecondaryAlt
                            type="button"
                            onClick={decreaseCount}
                            disabled={currentCount <= minCount}
                            className={styles.valueButton}
                            title={'Velg færre'}
                            aria-label={`Velg færre av ${reward.title}`}
                            trackingCategory={'reward-decrease-count'}
                            trackingAction={'click'}
                            trackingParameters={{
                                rewardId: reward?.id,
                                'project-id': project?.id,
                            }}
                        >
                            <i className="fa-regular fa-minus"></i>
                        </RoundedButtonSecondaryAlt>
                        <div className={styles.selectedCount} aria-live="polite">
                            {currentCount}
                        </div>

                        <RoundedButtonSecondaryAlt
                            type="button"
                            onClick={increaseCount}
                            className={styles.valueButton}
                            disabled={currentCount >= maxCount}
                            title={'Velg flere'}
                            aria-label={`Velg flere av ${reward.title}`}
                            trackingCategory={'reward-increase-count'}
                            trackingAction={'click'}
                            trackingParameters={{
                                rewardId: reward?.id,
                                'project-id': project?.id,
                            }}
                        >
                            <i className="fa-regular fa-plus"></i>
                        </RoundedButtonSecondaryAlt>
                    </div>
                </>
            ) : (
                <div />
            )}
            {cartItem ? (
                <RoundedButtonSecondary
                    trackingCategory={'shoppingcart-remove'}
                    trackingAction={'click'}
                    trackingParameters={{
                        rewardId: reward?.id,
                        count: currentCount,
                        'project-id': project?.id,
                        'show-more-modal-button': showReadMoreModalButton,
                        'product-category-id': reward?.product_reward_category_id,
                    }}
                    onClick={() => {
                        removeFromShoppingCart();
                        if (onAfterClick) {
                            onAfterClick();
                        }
                    }}
                    className={styles.iconButton}
                >
                    <Icon fileUrl={removeShoppingCart} />
                    <div>Fjern</div>
                </RoundedButtonSecondary>
            ) : (
                <RoundedButtonPrimary
                    trackingCategory={'shoppingcart-add'}
                    trackingAction={'click'}
                    trackingParameters={{
                        rewardId: reward?.id,
                        count: currentCount,
                        'project-id': project?.id,
                        lottery: isRewardLottery(reward),
                        'show-more-modal-button': showReadMoreModalButton,
                        'product-category-id': reward?.product_reward_category_id,
                    }}
                    onClick={(e) => {
                        if (onClick) {
                            onClick(e);
                        } else {
                            addToShoppingCart();
                        }
                        if (onAfterClick) {
                            onAfterClick();
                        }
                    }}
                    disabled={!canBuy}
                    className={styles.iconButton}
                    aria-label={`${reward.title} ${supportButtonText}`}
                >
                    {buttonIcon ? (
                        <>
                            <Icon fileUrl={buttonIcon} className={styles.blueButtonIcon} />
                            <div>{supportButtonText}</div>
                        </>
                    ) : (
                        supportButtonText
                    )}
                </RoundedButtonPrimary>
            )}
        </fieldset>
    );
};
